import { supabase } from '../supabaseClient';
import { resolveUnit, checkCompatibility, unitLabel, typeInfo } from '../utils/equipmentCompat';
import { fetchFleet, fetchRules } from './equipmentService';
import { getDefaultUnitSystem } from '../utils/units';

// __EQUIP_COMPAT__ (2026-10-04) DriverSelector's list (Edit job): each driver's unit is
// checked with the ONE equipment check (utils/equipmentCompat.js), plus the hours
// projection that was here before.
//
// What it replaced: drivers were read with `default_combination:truck_trailer_combinations(...)`,
// but the link runs the other way (combinations.default_driver_id -> drivers), so that
// embed came back as a LIST, `.truck` / `.trailer` were always undefined and no size was
// ever compared; it also took loads.weight_kg (pounds) as kilograms and printed
// "kg x 2.2" as pounds. RouteBuilder read drivers with no combination at all, so every
// drag said "No truck-trailer combination assigned".

const driverCompatibilityService = {
  async getDriversWithCompatibility(loadRequirements, scheduledDate = null) {
    const targetDate = scheduledDate || new Date().toISOString().split('T')[0];

    const [{ data: drivers, error: driversError }, fleet, rules] = await Promise.all([
      supabase.from('drivers').select('*, user:user_id(name)').eq('is_available', true),
      fetchFleet(),
      fetchRules(),
    ]);

    if (driversError) {
      console.error('Error fetching drivers:', driversError);
      return [];
    }

    const { data: driverLoads, error: loadsError } = await supabase
      .from('loads')
      .select('driver_id, estimated_duration_hours, scheduled_date, created_at')
      .not('status', 'in', '("complete","cancelled","billed","archived","ready_to_bill")')
      .not('driver_id', 'is', null);

    if (loadsError) {
      console.error('Error fetching driver loads:', loadsError);
    }

    const driverHoursMap = {};
    (driverLoads || []).forEach(load => {
      const loadDate = load.scheduled_date || load.created_at?.split('T')[0];
      if (loadDate === targetDate) {
        if (!driverHoursMap[load.driver_id]) {
          driverHoursMap[load.driver_id] = 0;
        }
        driverHoursMap[load.driver_id] += load.estimated_duration_hours || 2;
      }
    });

    return (drivers || []).map(driver => {
      const compatibility = this.checkCompatibility(driver, loadRequirements, fleet, rules, targetDate);
      const projectedHours = driverHoursMap[driver.id] || 0;
      // __EQUIP_V2__ company day limit from Settings -> Equipment rules (default 12 h on duty)
      const maxHours = driver.max_daily_hours || (rules && rules.timing && rules.timing.company_day_hours) || 12;
      const estimatedLoadHours = loadRequirements.estimated_duration_hours || 2;
      const hoursAfterLoad = projectedHours + estimatedLoadHours;

      let hoursStatus = 'ok';
      let hoursWarning = null;

      if (hoursAfterLoad > maxHours) {
        hoursStatus = 'over_limit';
        hoursWarning = `Would exceed ${maxHours}hr limit (${hoursAfterLoad.toFixed(1)}hrs total)`;
      } else if (hoursAfterLoad > maxHours - 1) {
        hoursStatus = 'near_limit';
        hoursWarning = `Near ${maxHours}hr limit (${hoursAfterLoad.toFixed(1)}hrs after this load)`;
      }

      return {
        ...driver,
        default_combination: compatibility.unitInfo,
        compatibility,
        projectedHours,
        hoursAfterLoad,
        maxHours,
        hoursStatus,
        hoursWarning,
        isFullyCompatible: compatibility.isCompatible && compatibility.warnings.length === 0 && hoursStatus === 'ok',
        // __EQUIP_V2__ limits warn, they do not block: only a missing driver/unit stops the pick
        isSelectable: compatibility.canAssign
      };
    });
  },

  // -> { isCompatible, reasons[], warnings[], details{weight,dimensions}, unitInfo, result }
  checkCompatibility(driver, loadRequirements, fleet, rules, date) {
    const unit = fleet && !fleet.error ? resolveUnit({ driverId: driver && driver.id, fleet,
      day: date || (loadRequirements && loadRequirements.scheduled_date) || undefined }) : null;
    const res = checkCompatibility({
      unit, loads: [loadRequirements || {}], rules, units: getDefaultUnitSystem(),
      driverName: driver ? (driver.name || (driver.user && driver.user.name)) : null,
      date: date || (loadRequirements && loadRequirements.scheduled_date) || undefined,
      driverId: driver ? driver.id : undefined,   // __SELF_UNLOAD__ his abilities
    });
    const cap = res.unit;
    const units = getDefaultUnitSystem();
    const ft = (v) => (v == null ? '?' : units === 'metric' ? (v * 0.3048).toFixed(2) + ' m' : v.toFixed(1) + ' ft');
    const lb = (v) => (v == null ? '?' : units === 'metric' ? Math.round(v / 2.20462262).toLocaleString() + ' kg' : Math.round(v).toLocaleString() + ' lb');
    return {
      isCompatible: res.ok,
      canAssign: res.canAssign,
      needsReason: res.needsReason,
      reasons: [...res.stops, ...res.blocking, ...res.warnings].map((x) => x.message),
      warnings: res.infos.map((x) => x.message),
      details: {
        weight: { status: cap ? 'known' : 'unknown', message: cap ? 'Payload ' + lb(cap.payloadLb) + (cap.payloadFrom ? ' (' + cap.payloadFrom + ')' : '') : 'No unit' },
        dimensions: { status: cap ? 'known' : 'unknown', message: cap ? 'Deck ' + ft(cap.deckLengthFt) + ' x ' + ft(cap.deckWidthFt) + (cap.deckHeightFt != null ? ', deck height ' + ft(cap.deckHeightFt) : '') + (cap.insideHeightFt != null ? ', inside height ' + ft(cap.insideHeightFt) : '') : 'No unit' },
      },
      unitInfo: unit ? {
        combination_name: unitLabel(unit),
        truck: unit.truck ? { truck_type: typeInfo('truck', unit.truck.truck_type).label } : null,
        trailer: unit.trailer ? { trailer_type: typeInfo('trailer', unit.trailer.trailer_type).label } : null,
      } : null,
      result: res,
    };
  },

  sortDriversByCompatibility(drivers) {
    return [...drivers].sort((a, b) => {
      if (a.isFullyCompatible && !b.isFullyCompatible) return -1;
      if (!a.isFullyCompatible && b.isFullyCompatible) return 1;

      if (a.isSelectable && !b.isSelectable) return -1;
      if (!a.isSelectable && b.isSelectable) return 1;

      if (a.hoursStatus === 'ok' && b.hoursStatus !== 'ok') return -1;
      if (a.hoursStatus !== 'ok' && b.hoursStatus === 'ok') return 1;

      return (a.projectedHours || 0) - (b.projectedHours || 0);
    });
  }
};

export default driverCompatibilityService;
